import {
  Button,
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@monksflow/monks-ui';
import { AlertTriangleIcon } from 'lucide-react';
import { extractStatus } from './extractStatus';
import { useTranslation } from 'react-i18next';

interface Props {
  error: unknown;
  onRetry: () => void;
  onClose: () => void;
}

/**
 * Error state for the artifact preview modal.
 *
 * 404 means the artifact was deleted/detached after the Library grid rendered.
 * In that case we surface a Close-only flow; the modal's universal close
 * handler (`ArtifactPreviewModal.handleOpenChange`) owns the 404 list
 * invalidation so every dismiss path (ESC, backdrop, X, this Close button)
 * fires it exactly once. All other errors get a generic message with a Retry
 * action wired to `query.refetch()`.
 */
export function ArtifactPreviewError(props: Props) {
  const { t } = useTranslation('projects');

  const status = extractStatus(props.error);
  const isNotFound = status === 404;

  return (
    <Empty role="alert" data-testid="artifact-preview-error" className="p-6">
      <EmptyHeader>
        <EmptyMedia variant="icon">
          <AlertTriangleIcon className="size-6 text-destructive" />
        </EmptyMedia>
        <EmptyTitle>
          {isNotFound
            ? t('projectDetail.artifactPreviewError.artifactNoLongerAttached')
            : t('projectDetail.artifactPreviewError.couldnTLoadArtifact')}
        </EmptyTitle>
        <EmptyDescription>
          {isNotFound
            ? t('projectDetail.artifactPreviewError.thisArtifactWasRemovedFrom')
            : t('projectDetail.artifactPreviewError.somethingWentWrongLoadingThis')}
        </EmptyDescription>
      </EmptyHeader>
      <EmptyContent>
        {isNotFound ? (
          <Button variant="outline" size="default" onClick={props.onClose}>
            {t('projectDetail.artifactPreviewError.close')}
          </Button>
        ) : (
          <Button variant="outline" size="default" onClick={props.onRetry}>
            {t('projectDetail.artifactPreviewError.tryAgain')}
          </Button>
        )}
      </EmptyContent>
    </Empty>
  );
}
